import pick from 'lodash.pick';

import type { AsyncStore } from '@libs/common-node';
import { SelectedPersonalizationKey, SelectedValueKey } from '@libs/algolia';
import type {
    MappedProductVariant,
    SelectedVariantKeys,
    SelectedVariants,
} from '@libs/algolia';

import { useInventories } from '@app/pdp/entities/inventories';
import { checkProductVariantStock } from '@app/pdp/components/ProductVariant/utils';

import { useProductDetails } from './useProductDetails';

const filteredSelectedValueKeys = Object.values(SelectedValueKey);
const personalizationKeys = Object.values(SelectedPersonalizationKey);

const findVariant = (
    variants: MappedProductVariant[],
    selectedVariant: Partial<SelectedVariants>
) => {
    const isVariantMatch = (
        variant: MappedProductVariant,
        selectedVariants: Partial<SelectedVariants>
    ) =>
        Object.entries(selectedVariants).every(
            ([key, value]) => variant.variants?.[key]?.value === value
        );

    return variants.find(variant => isVariantMatch(variant, selectedVariant));
};

export const useInStockProductVariant = (
    variantKey: SelectedVariantKeys,
    value: string,
    selectedStore: AsyncStore
) => {
    const { variants, selectedDeliveryMethod, selectedVariants } =
        useProductDetails();

    const { inventories } = useInventories();

    const isPersonalizationKey = personalizationKeys.includes(
        variantKey as SelectedPersonalizationKey
    );

    // For personalization keys we want to return true as we want to always display them as being in stock
    if (isPersonalizationKey) {
        return true;
    }

    // Filter personalization keys
    const { ...selectedVariant } = pick(
        selectedVariants,
        filteredSelectedValueKeys
    );

    const variant = findVariant(variants, {
        ...selectedVariant,
        [variantKey]: value,
    });

    const isVariant =
        variant !== undefined && Boolean(Object.keys(variant).length);

    return (
        isVariant &&
        checkProductVariantStock(
            selectedDeliveryMethod,
            variant,
            inventories,
            selectedStore
        )
    );
};
